<script setup lang="ts">
import type {ProjectData} from "~/types/ProjectData";
import {computed, ref} from "vue";

const props = defineProps<{
  project: ProjectData
}>();

const appUser = useAppUser();
const toast = useToast();
const isLoading = ref(false);
const localRequestSent = ref<{ [key: string]: boolean }>({});

const isRequestSent = (type: "beta-reading" | "correction" | "illustration") => {
  return !!(props.project.collaboration_requests_status?.[type] || localRequestSent.value[type]);
};

const seeksCollaboration = computed(() => {
  return props.project.seeks_beta_reading || props.project.seeks_correction || props.project.seeks_illustration;
});

const authorName = computed(() => {
  return props.project.authors[0]?.name || "l'auteur";
});

async function sendCollaborationRequest(type: "beta-reading" | "correction" | "illustration") {
  if (!appUser.value) return;

  isLoading.value = true;
  try {
    await $api(`/api/project/${props.project.uuid}/collaboration-request`, {
      method: "POST",
      body: {
        user_uuid: appUser.value.uuid,
        type: type,
      },
    });

    localRequestSent.value[type] = true;

    toast.add({
      title: "Demande envoyée",
      description: "Ta proposition d'aide a bien été transmise.",
      color: "success",
    });
  } catch (error: any) {
    toast.add({
      title: "Erreur",
      description: error.data?.message || "Une erreur est survenue lors de l'envoi de ta demande.",
      color: "error",
    });
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div v-if="seeksCollaboration" class="space-y-4">
    <h3 class="text-h5 underline">Collaborations recherchées</h3>

    <div class="flex flex-col gap-3">
      <div v-if="project.seeks_beta_reading">
        <p>Un retour sur l'histoire, les personnages, le rythme</p>
        <UButton
            color="neutral"
            variant="outline"
            class="w-full justify-start"
            :loading="isLoading"
            :disabled="isRequestSent('beta-reading')"
            @click="sendCollaborationRequest('beta-reading')"
        >
          {{ isRequestSent("beta-reading") ? "Demande de bêta-lecture envoyée" : "Je postule pour être bêta-lecteur•rice" }}
        </UButton>
      </div>

      <div v-if="project.seeks_correction">
        <p>Une relecture de la langue, de l'orthographe et de la ponctuation</p>
        <UButton
            color="neutral"
            variant="outline"
            class="w-full justify-start"
            :loading="isLoading"
            :disabled="isRequestSent('correction')"
            @click="sendCollaborationRequest('correction')"
        >
          {{ isRequestSent("correction") ? "Demande de correction envoyée" : "Je postule pour être correcteur•rice" }}
        </UButton>
      </div>

      <div v-if="project.seeks_illustration">
        <p>Le design de l'illustration de couverture</p>
        <UButton
            color="neutral"
            variant="outline"
            class="w-full justify-start"
            :loading="isLoading"
            :disabled="isRequestSent('illustration')"
            @click="sendCollaborationRequest('illustration')"
        >
          {{ isRequestSent("illustration") ? "Demande de couverture envoyée" : "Je postule pour être illustrateur•rice" }}
        </UButton>
      </div>
    </div>

    <p class="text-sm text-gray-600">
      Tu peux aider {{ authorName }} ? Clique sur le bouton de ton choix et nous reviendrons vers toi.
    </p>
  </div>
</template>
